// RenameChatModal.tsx
import React, { useState } from "react";
import { useModalState } from "../../helpers/ModalProvider";
import { useGlobalState } from "../../helpers/GlobalStateProvider";
import { renameConversation } from "../chatbot/chatHistoryStorage";
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from "../../components/ui/dialog";
import { Input } from "@chakra-ui/react";
import { Button } from "../../components/ui/button";
import { Field } from "../../components/ui/field";

const RenameChatModal: React.FC = () => {
  const [isRenaming, setIsRenaming] = useState(false);

  const {
    isRenameChatModalVisible,
    renameChatId,
    renameChatValue,
    setRenameChatValue,
    closeRenameChatModal,
    setChatToRenameId,
    setChatOptionsId,
  } = useModalState();

  const { setChatHistory, chatHistory } = useGlobalState();

  const handleClose = () => {
    closeRenameChatModal();
  };

  const renameChat = async (chatId: number) => {
    if (renameChatValue.trim() !== "") {
      setIsRenaming(true);

      // Find the chat to get the agent conversation ID
      const currentChat = chatHistory.find((c: any) => c.id === chatId);
      const agentConversationId = currentChat?.agentConversationId;

      if (agentConversationId) {
        // Try to rename in the agent backend
        const success = await renameConversation(agentConversationId, renameChatValue.trim());

        if (!success) {
          if (process.env.NODE_ENV === 'development') console.warn("Failed to rename conversation in backend, updating locally only");
        }
      }

      // Update local state (whether backend succeeded or not)
      setChatHistory((prev: any) =>
        prev.map((chat: any) =>
          chat.id === chatId ? { ...chat, title: renameChatValue.trim() } : chat,
        ),
      );

      closeRenameChatModal();
      setRenameChatValue("");
      setChatToRenameId(null);
      setChatOptionsId(null);
      setIsRenaming(false);
    }
  };

  const handleRename = () => {
    if (renameChatId !== null && !isRenaming) {
      renameChat(renameChatId);
    } else if (!isRenaming) {
      closeRenameChatModal();
    }
  };

  return (
    <DialogRoot lazyMount open={isRenameChatModalVisible} onOpenChange={(e) => { if (!e.open) handleClose(); }}>
      <DialogContent
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isRenaming) {
            e.preventDefault();
            handleRename();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Rename chat</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody>
          <Field label="Chat name">
            <Input
              type="text"
              value={renameChatValue}
              onChange={(e) => setRenameChatValue(e.target.value)}
              placeholder="Enter new chat name"
              fontSize="sm"
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button colorPalette="purple" bg="#9333ea" onClick={handleRename} loading={isRenaming}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default RenameChatModal;
